/**
 * File previews, rendered in the browser.
 *
 * The API never holds a file's bytes in the normal flow — uploads go straight
 * from here to storage — so it cannot draw a preview either. This makes one: a
 * small WebP of an image, or of a PDF's first page. `upload.ts` sends it up the
 * same way it sends a file.
 */

import type * as PdfJsModule from 'pdfjs-dist';

import { canvasSurface, drawTo, encodeWithin } from './transcode';

/**
 * Long edge, in pixels. The largest place a preview is drawn is the file
 * dialog, at about a third of this, so it stays sharp on a dense screen.
 */
const PREVIEW_EDGE = 640;

/** The API's ceiling. Real previews land at a tenth of it. */
const MAX_PREVIEW_BYTES = 256 * 1024;

const QUALITY_LADDER = [0.8, 0.65, 0.5];

async function imagePreview(source: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, PREVIEW_EDGE / Math.max(bitmap.width, bitmap.height));
    return await encodeWithin(
      drawTo(
        bitmap,
        Math.max(1, Math.round(bitmap.width * scale)),
        Math.max(1, Math.round(bitmap.height * scale)),
      ),
      MAX_PREVIEW_BYTES,
      QUALITY_LADDER,
    );
  } finally {
    bitmap.close();
  }
}

type PdfJs = typeof PdfJsModule;
let pdfjs: Promise<PdfJs> | null = null;

/** Loaded on first use: pdf.js is large, and only a PDF preview needs it. */
function loadPdfJs(): Promise<PdfJs> {
  pdfjs ??= Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]).then(([library, worker]) => {
    library.GlobalWorkerOptions.workerSrc = worker.default;
    return library;
  });
  // A failed load (a dropped connection mid-deploy) should not stick for the session.
  pdfjs.catch(() => {
    pdfjs = null;
  });
  return pdfjs;
}

async function pdfPreview(source: Blob): Promise<Blob> {
  const library = await loadPdfJs();
  // Served by the `pdfjsAssets` plugin in vite.config.ts.
  const assets = `${import.meta.env.BASE_URL}assets/pdfjs-${library.version}/`;
  const task = library.getDocument({
    data: new Uint8Array(await source.arrayBuffer()),
    wasmUrl: `${assets}wasm/`,
    standardFontDataUrl: `${assets}standard_fonts/`,
    cMapUrl: `${assets}cmaps/`,
    iccUrl: `${assets}iccs/`,
  });

  try {
    const pdf = await task.promise;
    const page = await pdf.getPage(1);
    const natural = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({
      scale: PREVIEW_EDGE / Math.max(natural.width, natural.height),
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    await page.render({ canvas, viewport }).promise;
    return await encodeWithin(canvasSurface(canvas), MAX_PREVIEW_BYTES, QUALITY_LADDER);
  } finally {
    await task.destroy();
  }
}

/** A WebP preview of an image or of a PDF's first page. Rejects if it cannot draw one. */
export function makePreview(source: Blob, mimeType: string): Promise<Blob> {
  return mimeType === 'application/pdf' ? pdfPreview(source) : imagePreview(source);
}
